<template>
  <div class="body-section mt-15">
    <div class="grade-selector d-flex align-center py-4 px-3 mt-5">
      <h4 class="white--text">Select Grade</h4>
      <div class="grade-select">
        <v-select
          :items="grades"
          flat
          solo
          label="Select Grade"
          class="d-flex align-self-center mb-2"
          v-model="selectedGrade"
          @change="fetchSubjects"
          return-object
          item-value="id"
          item-text="grade"
          hide-details
        ></v-select>
      </div>
      <v-spacer />
    </div>

    <v-row v-if="!loading" class="row-wrapper mx-3">
      <v-col class="subjects-wrapper" lg="4" md="4" sm="12">
        <h5 class="subjects-title mb-2">Subjects</h5>
        <NoData
          v-if="!loadingSubjects && !subjects.length"
          title="No Data"
          subtitle="No Subjects Found for this grade"
          :imageWidth="220"
        />
        <v-skeleton-loader max-width="300" type="card" v-if="loadingSubjects">
        </v-skeleton-loader>
        <v-card
          v-else
          v-for="sub in subjects"
          :key="sub.id"
          :class="`subject-card mb-2 ${
            sub.id === selectedSubject.id && 'selected-subject'
          }`"
          max-width="22rem"
          outlined
          @click="fetchTopics(sub)"
        >
          <v-list-item three-line>
            <v-list-item-avatar tile size="120">
              <v-avatar tile size="120" class="white&#45;&#45;text">
                <v-img v-if="sub.avatar" :src="sub.avatar" />
                <span v-else class="subject-icon">{{
                  sub.name.charAt(0).toUpperCase()
                }}</span>
              </v-avatar>
            </v-list-item-avatar>
            <v-list-item-content>
              <div
                :class="`text-overline mb-4 ${
                  sub.id === selectedSubject.id
                    ? 'white--text'
                    : 'primary--text'
                }`"
              >
                {{ sub.name }}
              </div>
              <v-list-item-subtitle
                :class="`text-overline mb-4 ${
                  sub.id === selectedSubject.id
                    ? 'white--text'
                    : 'primary--text'
                }`"
              >
                {{ sub.topics_count }} Topics
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-card>
      </v-col>

      <v-col class="topics-wrapper" lg="4" md="4" sm="12">
        <v-badge
          color="grey"
          :content="topics.length"
          class="subjects-title mb-5 mt-2"
        >
          Topics
        </v-badge>
        <h5 class="mb-2 primary--text">{{ selectedSubject.name }}</h5>
        <v-text-field
          prepend-inner-icon="mdi-magnify"
          placeholder="Find a topic"
          outlined
          class="topics-search"
          v-model="topicSearchText"
          :disabled="!selectedSubject.name"
        />
        <NoData
          v-if="!loadingTopics && !topics.length"
          title=""
          :subtitle="
            selectedSubject.name
              ? 'No Subjects Found for this grade'
              : 'Select Subject to Show Topics'
          "
          :imageWidth="220"
        />
        <v-skeleton-loader
          class=""
          max-width="300"
          type="card"
          v-if="loadingTopics || searchTopicsLoading"
        >
        </v-skeleton-loader>
        <v-card
          v-else
          v-for="topic in topics"
          :key="topic.id"
          :class="`subject-card mb-2 ${
            topic.uuid === selectedTopic.uuid && 'selected-subject'
          }`"
          max-width="25rem"
          elevation="0"
          @click="fetchSubTopics(topic)"
        >
          <v-list-item three-line>
            <v-list-item-avatar tile size="120" class="">
              <v-avatar tile color="" size="120" class="white&#45;&#45;text">
                <v-img v-if="topic.topic_cover" :src="topic.topic_cover" />
                <span v-else class="subject-icon white--text">{{
                  topic.title.charAt(0).toUpperCase()
                }}</span>
              </v-avatar>
            </v-list-item-avatar>
            <v-list-item-content>
              <div
                :class="`text-overline mb-4 ${
                  topic.uuid === selectedTopic.uuid
                    ? 'white--text'
                    : 'primary--text'
                }`"
              >
                {{ topic.title }}
              </div>
              <v-list-item-subtitle
                :class="`${
                  topic.uuid === selectedTopic.uuid
                    ? 'white--text'
                    : 'primary--text'
                }`"
                >{{ topic.overview }}</v-list-item-subtitle
              >
              <v-list-item-subtitle
                :class="`text-overline mb-4 ${
                  topic.uuid === selectedTopic.uuid
                    ? 'white--text'
                    : 'primary--text'
                }`"
                >{{ topic.sub_topic_count }} Sub Topics</v-list-item-subtitle
              >
            </v-list-item-content>
          </v-list-item>
        </v-card>
      </v-col>

      <v-col class="topics-wrapper" lg="3" md="3" sm="12">
        <h5 class="subjects-title">Subtopics</h5>
        <NoData
          v-if="!loadingSubTopics && !subTopics.length"
          title=""
          :subtitle="
            selectedTopic.name
              ? `No Sub Topics Found for ${selectedTopic.name}`
              : 'Please select a Topic to show sub topics'
          "
          :imageWidth="220"
        />
        <v-skeleton-loader
          class=""
          max-width="300"
          type="card"
          v-if="loadingSubTopics"
        >
        </v-skeleton-loader>
        <v-card
          v-else
          v-for="subTopic in subTopics"
          :key="subTopic.uuid"
          class="subject-card mb-2"
          max-width="25rem"
          elevation="0"
          :to="`/learning/${selectedTopic.uuid}/${subTopic.uuid}/lessons`"
        >
          <v-list-item three-line>
            <v-list-item-avatar tile size="80" class="">
              <v-avatar
                tile
                color="primary"
                size="80"
                class="white&#45;&#45;text"
              >
                <v-img
                  v-if="subTopic.sub_topic_cover"
                  :src="subTopic.sub_topic_cover"
                />
                <span v-else class="subject-icon white--text">{{
                  subTopic.title.charAt(0).toUpperCase()
                }}</span>
              </v-avatar>
            </v-list-item-avatar>
            <v-list-item-content>
              <div>{{ subTopic.title }}</div>
              <v-list-item-subtitle>{{
                subTopic.description
              }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-card>
      </v-col>
    </v-row>
    <FullScreenLoader v-else />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import FullScreenLoader from "@/components/FullScreenLoader.vue";
import NoData from "@/components/NoData.vue";
import HasAccountTypeAndUSerExistsMixin from "@/mixins/HasAccountTypeAndUSerExistsMixin";

export default {
  mixins: [HasAccountTypeAndUSerExistsMixin],
  components: {
    FullScreenLoader,
    NoData,
  },
  metaInfo() {
    return {
      title: "Guiomentor - Learning",
      url: window.location.href,
    };
  },
  data: () => ({
    showLogOutDialog: false,
    item: 1,
    drawerOpen: true,
    showMiniVariant: true,
    colors: ["green", "red", "secondary", "primary"],
    loading: true,
    grades: [],
    activeGradeId: null,
    activeGradeIndex: 0,
    loaders: [1, 2, 4, 5],
    subjects: [],
    loadingSubjects: false,
    selectedGrade: {},
    loadingTopics: false,
    selectedSubject: {},
    selectedTopic: {},
    topics: [],
    loadingSubTopics: false,
    subTopics: [],
    searchTopicsLoading: false,
    topicSearchText: "",
  }),
  computed: {
    ...mapGetters({
      isAuthenticated: "auth/GET_IS_AUTHENTICATED",
      user: "auth/GET_USER",
    }),
    miniVariant() {
      return this.drawerOpen && !this.$vuetify.breakpoint.xs;
    },
  },
  async mounted() {
    try {
      const { data } = await this.axios.get(
        "class_grade/?country_code=UG&limit=100"
      );
      this.grades = data.results;
      this.activeGradeId = data.results[0].id;
      this.selectedGrade = data.results[0];
    } catch (error) {
      this.$snackbar.showMessage({
        content: "Couldn't load grades",
        color: "error",
      });
      this.loading = false;
    } finally {
      this.loading = false;
    }
  },
  methods: {
    fetchSubjects(grade) {
      this.activeGradeId = grade.id;
    },
    async fetchTopics(subject) {
      if (subject.id === this.selectedSubject.id) {
        return;
      }
      try {
        this.loadingTopics = true;
        this.selectedSubject = subject;
        this.subTopics = [];
        this.selectedTopic = {};
        const { data } = await this.axios.get(
          `topics/?grade_level=${this.activeGradeId}&subject=${subject.id}&limit=100`
        );
        this.topics = data.results;
        this.loadingTopics = false;
      } catch (error) {
        this.$snackbar.showMessage({
          content: "Couldn't load topics for this subject",
          color: "error",
        });
        this.loadingTopics = false;
      }
    },
    async fetchSubTopics(topic) {
      if (topic.uuid === this.selectedTopic.uuid) {
        return;
      }
      try {
        this.selectedTopic = topic;
        this.loadingSubTopics = true;
        const { data } = await this.axios.get(`topics/${topic.uuid}/subtopics`);
        this.subTopics = data.results || [];
        this.loadingSubTopics = false;
      } catch (error) {
        this.$snackbar.showMessage({
          content: "Couldn't load sub topics for this topic",
          color: "error",
        });
        this.loadingSubTopics = false;
      }
    },
    generateLink(subject) {
      const grade = this.grades[this.activeGradeIndex] || {};
      const url = `/learning/grade/${grade.grade
        ?.split(" ")
        ?.join("-")
        ?.toLowerCase()}-${grade.id}/subject/${subject?.name
        ?.split(" ")
        ?.join("-")
        ?.toLowerCase()}-${subject.id}/topics`;
      return url;
    },
  },
  watch: {
    async activeGradeId(val) {
      this.loadingSubjects = true;
      if (val) {
        try {
          const { data } = await this.axios.get(
            `subjects?grade=${val}&limit=100`
          );
          this.subjects = data.results;
        } catch (error) {
          this.$snackbar.showMessage({
            content: "Couldn't load subjects for this grade",
            color: "error",
          });
          this.loadingSubjects = false;
        } finally {
          this.loadingSubjects = false;
        }
      }
    },
    async topicSearchText(val) {
      if (val.trim().length) {
        try {
          this.searchTopicsLoading = true;
          const { data } = await this.axios.get(
            `topics?subject=${this.selectedSubject.id}&search=${this.topicSearchText}`
          );
          this.topics = data.results;
          this.searchTopicsLoading = false;
        } catch (error) {
          this.$snackbar.showMessage({
            content: "Couldn't load topics",
            color: "error",
          });
          this.searchTopicsLoading = false;
        }
      } else {
        await this.fetchSubjects(this.selectedSubject);
      }
      // if (topic.uuid === this.selectedTopic.uuid) {
      //   return;
      // }
      console.log(val);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~vuetify/src/styles/settings/_variables";

.body-section {
  min-height: 90vh;
}
.topic-image {
  width: 10px;
  background: red;
  border-right: 1px solid rgb(84, 84, 84);
}
.subject-card {
  border-radius: 1rem;
  background: #ffffff;
  cursor: pointer;
}

.selected-subject {
  background: var(--v-primary-base);
  color: white;
}
.subjects-title {
  font-weight: 700;
  font-size: 24px;
  line-height: 29px;
  color: var(--v-primary-base);
}
.centered-section {
  background: var(--v-primary-base);
  min-height: 35vh;
}
.row-wrapper {
  // background:#E3E3E3;
  padding: 10px;
  border-radius: 1rem;
  margin-top: 0.4rem;
  justify-content: space-between;
}
.grade-selector {
  background: var(--v-primary-base);
  position: relative;
}
.grade-select {
  margin-left: 2rem;
  width: 223px;
  height: 40px;
}
.subjects-wrapper {
  background: #ffff;
  border-radius: 1rem;
  overflow-x: hidden;
  overflow-y: auto;
  height: 80vh;

}
.topics-search {
  background: #f9f9f9;
  border-radius: 16px;
}
.topics-wrapper {
  background: #ffff;
  border-radius: 1rem;
}
.subject-icon {
  font-size: 1.5rem;
  font-weight: bold;
}
@media #{map-get($display-breakpoints, 'sm-and-down')} {
  .add-margin-bottom-on-mobile {
    margin-bottom: 3em;
  }
}
</style>
